/* @venturewrist/shared — the single source of truth for cross-cutting types.
 *
 * Per §2 of the architecture proposal, one Zod schema defines the API contract,
 * client-side form validation, and the shared TS types. Phase 1 covers auth,
 * classes, teams, games, and the PvAI per-round decision payload. The strategy /
 * segment / distribution enums mirror the engine's own constants so an invalid
 * choice is rejected at the edge before it ever reaches the simulation.
 */
import { z } from 'zod';
// ── Roles & class mode ──────────────────────────────────────────────────────
export const Role = z.enum(['instructor', 'student']);
export const Opponents = z.enum(['ai', 'pvp', 'mixed']);
export const Grouping = z.enum(['solo', 'teams']);
export const ClassMode = z.object({
    grouping: Grouping,
    opponents: Opponents,
    teamSize: z.number().int().positive().default(3),
    aiCompanies: z.number().int().nonnegative().max(8).default(3),
});
// ── Engine-aligned enums (mirror engine.core constants) ─────────────────────
/** Class/strategy starting templates (engine STRATEGIES ids). */
export const Strategy = z.enum(['cost', 'premium', 'fitness', 'health', 'broad']);
/** Brand strategy (engine BRAND_STRATS ids). */
export const BrandStrat = z.enum(['range', 'unique']);
/** Product segment templates the engine can seed a new product from. */
export const Segment = z.enum(['budget', 'fitness', 'health', 'pro', 'fashion']);
/** Per-country go-to-market method (engine enterMethod ids; `online` is the default). */
export const DistMethod = z.enum(['dist', 'carrier', 'stores']);
export const GameStatus = z.enum(['setup', 'active', 'finished']);
// ── Auth ────────────────────────────────────────────────────────────────────
const email = z.string().email().max(254).transform((s) => s.trim().toLowerCase());
const password = z.string().min(8).max(200);
export const SignupInput = z.object({
    email,
    password,
    name: z.string().min(1).max(120),
    role: Role.default('student'),
});
// Login is a credential *check*, not account creation — no length policy here,
// or legitimately-created legacy/demo passwords could never sign in.
export const LoginInput = z.object({ email, password: z.string().min(1).max(200) });
// ── Tutorial progress (in-app guided tours; DESIGN-tutorial-system.md) ──────
/** One series' progress. `chapter`/`step` are 0-based resume pointers. */
export const TutorialSeriesProgress = z.object({
    chapter: z.number().int().min(0).max(40),
    step: z.number().int().min(0).max(40),
    /** User chose "Skip tour" — never auto-start again. */
    dismissed: z.boolean(),
    /** Reached the final step — never auto-start again (still restartable). */
    completed: z.boolean(),
});
/** Per-user tutorial progress persisted on User.tutorialState so it follows the
 *  user across devices. Keyed by a scope string: the instructor tour is keyed
 *  'instructor'; the student tour is keyed per team ('student:<teamId>') so a
 *  student replays it on each new team's first quarter. */
export const TutorialState = z.record(z.string().min(1).max(40), TutorialSeriesProgress);
/** UI theme. 'light' is the warm marketing-site look and the product default;
 *  'dark' is the original navy palette, opt-in. Stored on the user, so it
 *  follows them across devices. */
export const Theme = z.enum(['dark', 'light']);
export const PublicUser = z.object({
    id: z.string(),
    email: z.string(),
    name: z.string(),
    role: Role,
    /** Accessibility: minimize UI motion (charts/maps/transitions). */
    reduceMotion: z.boolean().nullable(),
    /** UI theme. Null = no choice made, so the client's default (light) applies. */
    theme: Theme.nullable(),
    /** Guided-tutorial progress per series (null until first touched). */
    tutorialState: TutorialState.nullable(),
});
/** Mutable user preferences (PATCH /auth/me). All fields optional — send only
 *  what changes. */
export const UpdatePreferencesInput = z.object({
    reduceMotion: z.boolean().optional(),
    theme: Theme.optional(),
    /** Full replacement of the tutorial-progress map. */
    tutorialState: TutorialState.optional(),
});
/** Append an activity-log event (currently tutorial instrumentation only).
 *  teamId is optional: instructor-console events have no team context. */
export const LogActivityInput = z.object({
    type: z.enum(['tutorial']),
    teamId: z.string().min(1).optional(),
    payload: z.record(z.string().min(1).max(60), z.unknown()).optional(),
});
// ── Classes ─────────────────────────────────────────────────────────────────
export const CreateClassInput = z.object({
    name: z.string().min(1).max(160),
    brief: z.string().max(5000).optional(),
    simLength: z.number().int().min(1).max(40).default(16),
    mode: ClassMode,
});
export const JoinClassInput = z.object({
    joinCode: z.string().min(4).max(12).transform((s) => s.trim().toUpperCase()),
});
// ── Instructor console (Phase 3) ─────────────────────────────────────────────
/** Update editable class settings (instructor owner). All fields optional. */
export const UpdateClassInput = z.object({
    name: z.string().min(1).max(160).optional(),
    brief: z.string().max(5000).nullable().optional(),
    simLength: z.number().int().min(1).max(40).optional(),
    gradeWeights: z.record(z.string().min(1).max(40), z.number().min(0).max(100)).optional(),
}).strict();
/** Set / override a student's grade for a class (instructor owner). */
export const SetGradeInput = z.object({
    components: z.record(z.string().min(1).max(40), z.number()).optional(),
    total: z.number().min(0).max(100).nullable().optional(),
});
// ── Assessment capture (Phase 3) ─────────────────────────────────────────────
/** Student submits an Academy knowledge-check attempt: the chosen option per
 *  question (question index → option index). The SERVER grades it against the
 *  engine's answer key — a client-computed score is never accepted. */
export const SubmitQuizInput = z.object({
    lessonId: z.string().min(1).max(60).optional(),
    answers: z.record(z.string().regex(/^\d{1,3}$/), z.number().int().min(0).max(16)),
});
/** Student writes/updates the strategy-journal entry for a quarter. */
export const SubmitJournalInput = z.object({
    round: z.number().int().min(0).max(64).nullable().optional(),
    text: z.string().min(1).max(10_000),
});
// ── Teams ───────────────────────────────────────────────────────────────────
export const CreateTeamInput = z.object({
    classId: z.string().min(1),
    name: z.string().min(1).max(120),
    solo: z.boolean().default(false),
});
export const JoinTeamInput = z.object({ teamId: z.string().min(1) });
// ── Games ───────────────────────────────────────────────────────────────────
export const CreateGameInput = z.object({
    teamId: z.string().min(1),
    strategy: Strategy,
    companyName: z.string().min(1).max(120),
    brandStrat: BrandStrat.default('range'),
    /** Instructor-only override of the class simLength; ignored for students. */
    maxRounds: z.number().int().min(1).max(40).optional(),
    /** Instructor-only explicit seed for reproducible runs; students always get a
     *  server-assigned one (a hand-picked seed would pre-select the random events). */
    seed: z.number().int().optional(),
});
// ── Staged new-product addressing ────────────────────────────────────────────
/** Working-game id of a product staged in this round's `newProducts` bundle:
 *  `'new:<index>'`. The client preview (buildWorkingGame) assigns these ids and
 *  the server's applyDecisions resolves them onto the products it creates from
 *  the same bundle — so same-quarter Pricing/Production/Marketing edits on a
 *  brand-new product resolve exactly as previewed. */
export const NEW_PRODUCT_ID_PREFIX = 'new:';
export const newProductIndex = (id) => id.startsWith(NEW_PRODUCT_ID_PREFIX) ? Number(id.slice(NEW_PRODUCT_ID_PREFIX.length)) : null;
// ── Per-round decision payload (PvAI; §5 parity path) ───────────────────────
//
// A structured representation of the levers a player sets before advancing a
// quarter. The server applies these onto the live game object through the
// engine's own mutators (the same paths the prototype UI drives) and then calls
// Engine.advance — the browser never computes results.
export const MarketingDecision = z.object({
    budget: z.number().min(0).max(50), // $M ad spend
    channel: z.string().min(1).max(40),
    creative: z.string().min(1).max(40),
    /** Campaign country group (engine country ids). Missing/empty = worldwide.
     *  Media cost scales with audience size, so focusing the same budget on fewer
     *  countries buys more awareness per buyer there (engine campaignConc, capped). */
    countries: z.array(z.string().min(1).max(8)).max(60).optional(),
});
/** Optional hardware spec for designing a product (engine COMPONENTS ids). */
export const ProductSpec = z
    .object({
    cpu: z.string().max(8),
    display: z.string().max(8),
    battery: z.string().max(8),
    casing: z.string().max(8),
    strap: z.string().max(8),
    size: z.string().max(8),
    style: z.string().max(16),
    color: z.string().max(16),
    sensors: z.array(z.string().max(8)).max(8),
})
    .partial();
/** A committed new hardware generation: a full spec cloned from generation `base`. */
export const DraftDecision = z.object({
    base: z.number().int().positive(),
    spec: ProductSpec,
});
export const GenerationDecision = z.object({
    gen: z.number().int().positive(),
    price: z.number().min(0).max(100000).optional(),
    production: z.number().min(0).max(10_000_000).optional(),
    /** Spec edit for a still-editable ("proto") generation — Design Studio. */
    spec: ProductSpec.optional(),
    /** Production screen: per-source order allocation (sourceId → k units). When
     *  present the engine routes this generation's order across these sources
     *  instead of auto-allocating; the sum becomes the generation's volume.
     *  `null` clears a split persisted from an earlier quarter (back to auto). */
    alloc: z.record(z.string().min(1).max(40), z.number().min(0).max(10_000)).nullable().optional(),
});
// ── Production network intents (Production screen) ──────────────────────────
/** Build a new owned factory in a city at a chosen capacity (k units/qtr). */
export const FactoryBuild = z.object({
    city: z.string().min(1).max(8),
    cap: z.number().int().min(50).max(500),
});
/** Operate on an existing owned factory. */
export const FactoryOp = z.object({
    id: z.string().min(1).max(40),
    op: z.enum(['expand', 'mothball', 'reactivate']),
});
/** A negotiated long-term volume commitment with a manufacturing partner. */
export const ContractDecision = z.object({
    partnerId: z.string().min(1).max(40),
    qty: z.number().int().min(40).max(400),
    dur: z.number().int().min(2).max(12),
});
/** Generation lifecycle action: scrap remaining inventory or resume a paused gen. */
export const GenOp = z.object({
    productId: z.string().min(1),
    gen: z.number().int().positive(),
    op: z.enum(['writeoff', 'resume']),
});
export const ProductDecision = z.object({
    id: z.string().min(1),
    name: z.string().min(1).max(120).optional(),
    /** Retarget the model's primary segment (Design Studio "make target"). */
    targetSeg: Segment.optional(),
    /** Retire the entire model line. */
    retire: z.boolean().optional(),
    mkt: MarketingDecision.optional(),
    gens: z.array(GenerationDecision).max(20).optional(),
    /** A committed new generation engineered this round (charges engineering cost,
     *  launches next quarter). The transient pre-commit "draft" stays client-side. */
    newGen: DraftDecision.optional(),
});
export const NewProductDecision = z.object({
    seg: Segment,
    name: z.string().min(1).max(120).optional(),
    price: z.number().min(0).max(100000).optional(),
    production: z.number().min(0).max(10_000_000).optional(),
    marketing: MarketingDecision.optional(),
    /** Hardware design choices applied to the new product's first generation. */
    spec: ProductSpec.optional(),
    /** Retarget after seeding: `seg` picks the design/price template, this only
     *  changes the segment the model is aimed at (Design Studio "make target"). */
    targetSeg: Segment.optional(),
});
export const SuppliersDecision = z
    .object({
    chips: z.string().optional(),
    display: z.string().optional(),
    battery: z.string().optional(),
    sensors: z.string().optional(),
    assembly: z.string().optional(),
})
    .partial();
export const RndDecision = z.object({
    budget: z.number().min(0).max(100_000_000).optional(),
    queue: z.array(z.string().min(1)).max(40).optional(),
    active: z.string().min(1).nullable().optional(),
});
export const DistributionEntry = z.object({
    country: z.string().min(1).max(8),
    method: DistMethod,
});
export const StoreBuild = z.object({
    country: z.string().min(1).max(8),
    count: z.number().int().min(1).max(50),
});
/** Build a new online-fulfilment warehouse in a city at a chosen capacity
 *  (units/qtr). Charged now; comes online after a build lead time. */
export const WarehouseBuild = z.object({
    city: z.string().min(1).max(8),
    cap: z.number().int().min(300_000).max(3_000_000),
});
/** Per-quarter financing actions (Financing screen). All staged and applied at
 *  advance: bank loans / bonds drawn, equity raised, shares bought back, and the
 *  dividend payout policy. */
export const LoanTenor = z.union([z.literal(4), z.literal(8), z.literal(12)]);
export const BondTenor = z.union([z.literal(12), z.literal(16), z.literal(20)]);
export const FinancingDecision = z.object({
    /** Bank facilities the Financing screen offers: 4q note, 8q term loan, 12q long-term loan. */
    loans: z.array(z.object({ tenor: LoanTenor, amount: z.number().min(0).max(10_000_000_000) })).max(10).optional(),
    /** Bonds: 12 / 16 / 20-quarter bullets. */
    bonds: z.array(z.object({ tenor: BondTenor, face: z.number().min(0).max(10_000_000_000) })).max(10).optional(),
    equityRaise: z.number().min(0).max(10_000_000_000).optional(),
    buyback: z.number().min(0).max(10_000_000_000).optional(),
    divRatio: z.number().min(0).max(0.8).optional(),
});
/** The full per-round decision bundle. Every field is optional: a player may
 *  submit a minimal round (e.g. just price tweaks) and the rest carries over. */
export const RoundDecisions = z.object({
    suppliers: SuppliersDecision.optional(),
    rnd: RndDecision.optional(),
    newProducts: z.array(NewProductDecision).max(10).optional(),
    products: z.array(ProductDecision).max(20).optional(),
    distribution: z.array(DistributionEntry).max(60).optional(),
    storeBuilds: z.array(StoreBuild).max(60).optional(),
    /** Close owned stores in a country ($60k severance each). */
    storeCloses: z.array(StoreBuild).max(60).optional(),
    flagship: z.array(z.string().min(1).max(8)).max(60).optional(),
    // ── Production network ──
    /** Order-allocation strategy when a generation isn't manually allocated. */
    allocMode: z.enum(['cost', 'rel']).optional(),
    /** New owned factories to start building this quarter. */
    factoryBuilds: z.array(FactoryBuild).max(10).optional(),
    /** Expand / mothball / reactivate existing factories. */
    factoryOps: z.array(FactoryOp).max(40).optional(),
    /** Negotiated partner volume commitments signed this quarter. */
    contracts: z.array(ContractDecision).max(20).optional(),
    /** Partner ids to request a (free, relationship-funded) capacity expansion from. */
    expansions: z.array(z.string().min(1).max(40)).max(20).optional(),
    /** Per-warehouse online-fulfilment shipment (warehouseId → k units). */
    whAlloc: z.record(z.string().min(1).max(40), z.number().min(0).max(100_000)).optional(),
    /** Force warehouse shipment back to auto (clears any manual whAlloc). */
    whAuto: z.boolean().optional(),
    /** Generation lifecycle actions (write off inventory / resume). */
    genOps: z.array(GenOp).max(40).optional(),
    // ── Marketing & brand ──
    /** Switch the company brand strategy (Marketing screen). Switching mid-game
     *  resets all brand ratings to zero (Cap2 ch.7); on a freshly founded company
     *  the starting brand is migrated to the new model without penalty. */
    brandStrat: BrandStrat.optional(),
    // ── Logistics & warehouse network ──
    /** Who pays online freight: absorb (company) or pass-through (customer). */
    freightMode: z.enum(['absorb', 'passthrough']).optional(),
    /** New online-fulfilment warehouses to start building this quarter. */
    whBuilds: z.array(WarehouseBuild).max(10).optional(),
    /** Expand an existing warehouse (warehouseId → added capacity units). */
    whExpand: z.record(z.string().min(1).max(40), z.number().min(0).max(10_000_000)).optional(),
    /** Warehouses to close this quarter (no refund; mirrors store closures). */
    whClose: z.array(z.string().min(1).max(40)).max(20).optional(),
    /** Financing actions (Financing screen): loans/bonds drawn, equity raised,
     *  shares bought back, dividend policy set. Applied at advance. */
    financing: FinancingDecision.optional(),
});
export const SubmitRoundInput = z.object({
    /** Round the client believes it is submitting for; the server rejects a
     *  mismatch to prevent double-advances / stale submissions. */
    round: z.number().int().positive(),
    decisions: RoundDecisions.default({}),
});
//# sourceMappingURL=index.js.map